import type {
  FeedbackSupplierProfile,
  MassRenewalDownloadFile,
  MassRenewalLineSkipReason,
  MassRenewalRunReport,
  MassRenewalSkipReason,
} from '@erp/rpc';
import type { TranslateFn, UploadFileRejection } from 'src/lib/pricelist-utils';

import {
  RENEWAL_PEEK_ROWS,
  type DroppedRenewalFile,
  type RenewalDropIssue,
  type RenewalSlot,
} from './classifyRenewalFiles';
import type { RenewalStartBlocker } from './massRenewalRunDisplay';

/**
 * The Mass Renewal card's wording for the run's client-neutral reasons and
 * the page's own states, so the view stays a layout. Every string is built
 * inside a function given `gt`, so the extractor sees it.
 */

/** A category of file, as the category menu and the "looks like" hint name it. */
export function renewalSlotText(slot: RenewalSlot, gt: TranslateFn): string {
  switch (slot) {
    case 'results':
      return gt('Results Workbook', {
        context:
          'Mass Renewal card: category of a dropped file, the Results Workbook (FINAL.xlsx)',
      });
    case 'feedback':
      return gt('Feedback File', {
        context: 'Mass Renewal card: category of a dropped file',
      });
    case 'exceptions':
      return gt('Exception List', {
        context:
          'Mass Renewal card: category of a dropped file; product hold lists are Exception Lists too',
      });
  }
}

/** What a dropped file was found to be under its category; null while it is still being read. */
export function fileKindText(
  check: DroppedRenewalFile['check'],
  gt: TranslateFn,
): string | null {
  switch (check.kind) {
    case 'reading':
      return null;
    case 'results':
      return renewalSlotText('results', gt);
    case 'feedback':
      return gt('Feedback · {code} {division}', {
        context:
          'Mass Renewal card: badge on a dropped Feedback File; {code} is the Division code (WT), {division} its name',
        code: check.division.code,
        division: check.division.name,
      });
    case 'exceptions':
      if (check.holdDivision)
        return gt('Product Holds · {code} {division}', {
          context:
            'Mass Renewal card: uploaded product list holds margin and core cost increases for every account and load in this Division, for this run only',
          code: check.holdDivision.code,
          division: check.holdDivision.name,
        });
      if (check.scope)
        return gt('Exception List · {code} {division}', {
          context:
            'Mass Renewal card: badge on a priced Exception List whose file name names a Division; its rows apply to that Division only',
          code: check.scope.code,
          division: check.scope.name,
        });
      return gt('Exception List · Divisions by row', {
        context:
          'Mass Renewal card: badge on a priced Exception List with no Division in its file name; each row’s DIVISION column decides, blank meaning every Division',
      });
    case 'not results':
      if (check.missing)
        return gt('Not a Results Workbook: missing {headers}', {
          context:
            'Mass Renewal card: a file in the Results Workbook category lacks some results header columns; {headers} lists them',
          headers: check.missing.join(', '),
        });
      return gt(
        'Not a Results Workbook: no results header in the first {rows} rows',
        {
          context:
            'Mass Renewal card: a file in the Results Workbook category has no results header row; {rows} is a number',
          rows: String(RENEWAL_PEEK_ROWS),
        },
      );
    case 'no feedback sheet':
      return gt('No Division Feedback sheet, such as “WT Feedback”', {
        context:
          'Mass Renewal card: a file in the Feedback File category has no sheet named by a Division code and Feedback',
      });
    case 'not an exception list':
      return gt(
        'Not an Exception List: its first sheet needs AS400 ITEM or Supplier Item, and SELL NET or Margin',
        {
          context:
            'Mass Renewal card: a file in the Exception List category lacks the columns an Exception List has; the capitalized words are column names',
        },
      );
    case 'has the results header':
      return gt('Has the results header: not an Exception List', {
        context:
          'Mass Renewal card: a file in the Exception List category is the Results Workbook (FINAL.xlsx)',
      });
    case 'conflicting file name':
      return gt(
        'Its name names two Divisions or another supplier: rename it to one Division of this supplier, or none, then add it again',
        {
          context:
            'Mass Renewal card: an Exception List whose file name has conflicting Division or supplier scope, which the run refuses',
        },
      );
    case 'unreadable':
      return gt('Could not be opened as .xlsx', {
        context:
          'Mass Renewal card: the browser could not read a dropped file as an Excel workbook',
      });
  }
}

/** The category a misplaced file seems to belong to, as a hint to change it. */
export function looksLikeText(slot: RenewalSlot, gt: TranslateFn): string {
  return gt('This looks like it belongs under {category}.', {
    context:
      'Mass Renewal card: hint under a file that does not fit its category; {category} is Results Workbook, Feedback File or Exception List',
    category: renewalSlotText(slot, gt),
  });
}

/** Why Start is disabled; null when the reason is plain from the card itself. */
export function renewalStartBlockerText(
  blocker: RenewalStartBlocker,
  gt: TranslateFn,
): string | null {
  if (typeof blocker !== 'string') return dropIssueText(blocker, gt);
  switch (blocker) {
    case 'active run':
      return gt('A run is in progress; Start is available once it finishes.', {
        context: 'Mass Renewal card: why the Start button is disabled',
      });
    case 'not allowed':
      return gt('Starting a run needs the Pricing create permission.', {
        context: 'Mass Renewal card: why the Start button is disabled',
      });
    case 'reading':
      return gt('Reading the dropped files…', {
        context:
          'Mass Renewal card: files are still being recognized before Start',
      });
    case 'starting':
      return null;
  }
}

/** Why one dropped file holds Start: it does not fit its category, or could not be opened. */
function fileIssueText(
  issue: Extract<
    RenewalDropIssue,
    { issue: 'file does not fit its category' | 'file could not be opened' }
  >,
  gt: TranslateFn,
): string {
  switch (issue.issue) {
    case 'file does not fit its category':
      if (issue.missing)
        return gt(
          '{name} is missing the results columns {headers}; fix them in the workbook or change its category.',
          {
            context:
              'Mass Renewal card: why Start is disabled; {name} is the dropped Results Workbook, {headers} the header columns it lacks',
            name: issue.fileName,
            headers: issue.missing.join(', '),
          },
        );
      return gt(
        '{name} does not fit its category; change its category or remove it.',
        {
          context:
            'Mass Renewal card: why Start is disabled; {name} is a dropped file name',
          name: issue.fileName,
        },
      );
    case 'file could not be opened':
      return gt('{name} could not be opened as .xlsx; remove it.', {
        context:
          'Mass Renewal card: why Start is disabled; {name} is a dropped file the browser could not read',
        name: issue.fileName,
      });
  }
}

function dropIssueText(
  issue: Exclude<RenewalStartBlocker, string>,
  gt: TranslateFn,
): string {
  switch (issue.issue) {
    case 'file does not fit its category':
    case 'file could not be opened':
      return fileIssueText(issue, gt);
    case 'no results workbook':
      return gt('Add the Results Workbook.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'several results workbooks':
      return gt('{count} Results Workbooks were dropped; remove all but one.', {
        context: 'Mass Renewal card: why Start is disabled',
        count: String(issue.count),
      });
    case 'no feedback file':
      return gt('Drop at least one filled-in Feedback File.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'too many feedback files':
      return gt('A run takes at most nine Feedback Files.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'division has two feedback files':
      return gt('Two Feedback Files are for {code}; remove one.', {
        context:
          'Mass Renewal card: why Start is disabled; {code} is a Division code (WT)',
        code: issue.code,
      });
    case 'several exception lists':
      return gt('{count} Exception Lists were dropped; the limit is 20.', {
        context: 'Mass Renewal card: why Start is disabled',
        count: String(issue.count),
      });
  }
}

/** Why the run did not build a Division. */
export function divisionSkipReasonText(
  reason: MassRenewalSkipReason,
  gt: TranslateFn,
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string,
  /** For a Feedback File that does not match: the share of its rows that agreed (0–1). */
  share?: number,
): string {
  switch (reason) {
    case 'no feedback file':
      return gt('No Feedback File', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'two feedback files name this Division':
      return gt('Two Feedback Files name this Division', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'tab not in the Results Workbook':
      return gt('Not in {results}', {
        context:
          'Mass Renewal card: why a Division was not built; {results} names the Results Workbook',
        results,
      });
    case 'tab not in the Division table':
      return gt('Not in the Division table', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'feedback file does not match the results':
      return gt(
        'Feedback File does not match {results}: only {percent}% of its Loads have the same Quote SPA',
        {
          context:
            'Mass Renewal card: why a Division was not built; the Feedback File looks made for another supplier’s results. {results} names the Results Workbook, {percent} is a whole number',
          results,
          percent: String(Math.round((share ?? 0) * 100)),
        },
      );
  }
}

/** Why lines were left out of both uploads before pricing. */
export function lineSkipReasonText(
  reason: MassRenewalLineSkipReason,
  gt: TranslateFn,
): string {
  switch (reason) {
    case 'no AS400 Item':
      return gt('no AS400 Item', {
        context: 'Mass Renewal card: why lines were left out of the upload',
      });
    case 'blank Pricing Factor':
      return gt('blank Pricing Factor', {
        context: 'Mass Renewal card: why lines were left out of the upload',
      });
    case 'blank FINAL PRIOR MARGINS':
      return gt('blank FINAL PRIOR MARGINS', {
        context:
          'Mass Renewal card: why lines were left out of the upload; FINAL PRIOR MARGINS is a column name',
      });
    case 'blank quote cost':
      return gt('blank quote cost', {
        context:
          'Mass Renewal card: why lines were left out of the upload; the QUOTE COST PER EACH column is blank',
      });
    case 'zero quote cost':
      return gt('zero quote cost', {
        context:
          'Mass Renewal card: why lines were left out of the upload; the QUOTE COST PER EACH column is 0',
      });
  }
}

type UnmatchedExceptionRow =
  MassRenewalRunReport['unmatchedExceptionRows'][number];

/** Why an Exception List row priced no line. */
export function unmatchedExceptionReasonText(
  row: Pick<UnmatchedExceptionRow, 'reason' | 'failure'>,
  gt: TranslateFn,
): string {
  switch (row.reason) {
    case 'no line with this item':
      return gt('no line has this item', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; no line of the built Divisions has its AS400 item',
      });
    case 'no line matched its filters':
      return gt('no line matched its Account, Group, Load, SPA or Division', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; lines have its item but not its other filters',
      });
    case 'matched only lines that failed':
      return gt('matched only lines that failed: {failure}', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; {failure} is the first failed line’s problem, from the server',
        failure: row.failure ?? '',
      });
    case 'matched only lines of a skipped Division':
      return gt(
        'matched only lines of a Division that was skipped (its Feedback File does not match the results)',
        {
          context:
            'Mass Renewal card: why an Exception List row priced no line; the only lines it matched belong to a Division the run skipped',
        },
      );
  }
}

/** "3 no AS400 Item · 1 blank Pricing Factor": the skipped lines by reason. */
export function skippedBreakdownText(
  skipped: Readonly<Record<MassRenewalLineSkipReason, number>>,
  gt: TranslateFn,
): string {
  return (Object.entries(skipped) as [MassRenewalLineSkipReason, number][])
    .flatMap(([reason, count]) =>
      count > 0 ? [`${count} ${lineSkipReasonText(reason, gt)}`] : [],
    )
    .join(' · ');
}

/** A Division's download button label. */
export function downloadFileText(
  file: MassRenewalDownloadFile,
  gt: TranslateFn,
): string {
  switch (file) {
    case 'upload':
      return gt('Upload CSV', {
        context:
          "Mass Renewal card: button that downloads a Division's AS400 upload CSV",
      });
    case 'exceptions':
      return gt('Exceptions CSV', {
        context:
          "Mass Renewal card: button that downloads a Division's Exception List lines CSV",
      });
    case 'report':
      return gt('Report', {
        context:
          "Mass Renewal card: button that downloads a Division's Renewal Report workbook",
      });
  }
}

/** The run reads `.xlsx` only; said at the drop, not after an upload. */
export function validateRenewalFile(
  file: File,
  gt: TranslateFn,
  supplier: FeedbackSupplierProfile,
): UploadFileRejection | null {
  if (/\.xlsx$/i.test(file.name)) return null;
  return {
    kind: 'unsupported',
    title: gt('Unsupported file format', {
      context:
        "toast title when a non-.xlsx file is dropped on a supplier's Mass Renewal card",
    }),
    message: gt(
      '{supplier} Mass Renewal reads .xlsx workbooks only: {results}, the Feedback Files and the Exception Lists.',
      {
        context:
          "toast message when a non-.xlsx file is dropped on a supplier's Mass Renewal card; {supplier} is the supplier (Hubbell, ABB), {results} names its Results Workbook",
        supplier: supplier.name,
        results: supplier.resultsFileLabel,
      },
    ),
  };
}
